/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useOpportunity.ts
 * PURPOSE: Load mock opportunity, optimistic field updates, dirty tracking, batch save
 * DEPENDENCIES: react, ../context/CrmContext, ../data/opportunityDrawerOptions, ../data/scheduleModalCatalog, ../types/opportunity.types
 * PUBLIC_API: useOpportunity, UseOpportunityResult
 * SIDE_EFFECTS: Calls onSave from saveAll or savePartial; mock load uses setTimeout
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 420/450
 * RECENT: 2026-05-26 — UUID deals not in list cache fall back to **`crmGet('deals', id)`** (drawer load after deep link / race).
 * RECENT_PRIOR: 2026-05-11 — New-deal sentinel: **`savePartial`** and **`saveAll`** are local-draft only (no **`onSave`**); **`commitNewDeal`** is the single path to POST after validation (GitHub #84)
 * RECENT_PRIOR: 2026-05-11 New-deal sentinel — persist payload carries implicit **`name`** from merged local row when **`patch`** omits **`name`** (**`savePartial`** updater + **`saveAll`**)
 * RECENT_PRIOR: 2026-05-08 [2.2.878] **`retryLoad`** re-runs catalog resolve for current id — root **`CHANGELOG` [2.2.878]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.876] CRM persistence UX compliance — root **`CHANGELOG` [2.2.876]`**
 * RECENT_PRIOR: 2026-05-01 [2.2.760] **`stageHistory`** (GET **`/api/v1/crm/deals/{id}/stage-history`** or demo rows) — CHANGELOG [2.2.760]
 * RECENT_PRIOR: 2026-04-30 [2.2.732] Loads from **`crmOpportunities`** via ref — CHANGELOG [2.2.732]
 */

import { crmGet, isCrmConflictError } from '@shared/lib/frontend/api/crmApi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDealStageHistory } from '../api/crmDealStageHistoryApi';
import { useCrm } from '../context/CrmContext';
import { MOCK_SCHEDULE_OWNERS } from '../data/scheduleModalCatalog';
import { useCrmTerritoryOptions } from './useCrmTerritoryOptions';
import type { StageHistoryEntry } from '../types/crm.types';
import type { Opportunity } from '../types/opportunity.types';
import { buildDemoStageHistory } from '../utils/crmStageHistory';
import { isUuid, mapSalesOpportunityRowToCrm } from './crmEntityMappers';

/** Field-level messages from **`commitNewDeal`** client validation or generic **`form`**. */
export type NewDealCommitErrors = Partial<
  Record<'name' | 'accountId' | 'closeDateISO' | 'amount' | 'form', string>
> & { existingDealId?: string };

export type NewDealCommitResult =
  | { ok: true; opportunity: Opportunity }
  | { ok: false; errors: NewDealCommitErrors };

export type UseOpportunityResult = {
  opportunity: Opportunity | null;
  loading: boolean;
  error: Error | null;
  /** Re-run the mock or catalog resolve for the current `opportunityId` (e.g. after a transient failure or list refresh). */
  retryLoad: () => void;
  /** Stage timeline entries for `StageHistoryTimeline` (server-backed UUID deals or demo synthesis). */
  stageHistory: StageHistoryEntry[];
  updateField: <K extends keyof Opportunity>(key: K, value: Opportunity[K]) => void;
  saveAll: () => Promise<void>;
  /** Persists a partial update immediately (per-field save from InlineEditableField). */
  savePartial: (patch: Partial<Opportunity>) => Promise<void>;
  /** Sentinel-only: validates required fields then **`onSave`** once to POST the deal (see GitHub #84). */
  commitNewDeal: () => Promise<NewDealCommitResult>;
  isDirty: (key: keyof Opportunity | string) => boolean;
  /** True when any field is pending batch save (via `updateField` without `savePartial`). */
  hasUnsavedChanges: boolean;
};

function cloneOpportunity(row: Opportunity): Opportunity {
  return {
    ...row,
    tags: row.tags ? [...row.tags] : undefined,
  };
}

/** Drawer “create deal” sentinel — not persisted to mock list until save is wired to API. */
export const CRM_NEW_OPPORTUNITY_ID = '__crm_new__';

function createBlankOpportunity(): Opportunity {
  const now = new Date().toISOString();
  /** Close date is unset until the user picks one — avoids phantom pipeline rows from implicit defaults. */
  return {
    id: CRM_NEW_OPPORTUNITY_ID,
    name: '',
    accountId: '',
    accountName: '',
    ownerId: 'user-1',
    ownerName: 'Morgan Lee',
    stage: 'prospecting',
    amount: 0,
    weightedAmount: 0,
    closeDateISO: '',
    probability: 10,
    source: 'unknown',
    createdAtISO: now,
    updatedAtISO: now,
  };
}

function applyDerivedFields(next: Opportunity): Opportunity {
  const amount = Number(next.amount);
  const probability = Number(next.probability);
  if (Number.isFinite(amount) && Number.isFinite(probability)) {
    next.weightedAmount = Math.round((amount * probability) / 100);
  }
  return next;
}

export function useOpportunity(
  opportunityId: string | null,
  onSave: (updated: Partial<Opportunity>) => Promise<Opportunity | void | undefined>
): UseOpportunityResult {
  const { crmOpportunities } = useCrm();
  const { territoryLabel } = useCrmTerritoryOptions();
  const crmOpportunitiesRef = useRef(crmOpportunities);
  crmOpportunitiesRef.current = crmOpportunities;

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadRetryNonce, setLoadRetryNonce] = useState(0);
  const [stageHistory, setStageHistory] = useState<StageHistoryEntry[]>([]);
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(() => new Set());
  const loadGenRef = useRef(0);
  const histGenRef = useRef(0);
  const opportunityRef = useRef<Opportunity | null>(null);

  useEffect(() => {
    if (!opportunityId) {
      setOpportunity(null);
      setLoading(false);
      setError(null);
      setDirtyKeys(new Set());
      setStageHistory([]);
      return;
    }
    /** New deal opens from header immediately — no artificial delay (drawer must not stay empty or unmounted one frame). */
    if (opportunityId === CRM_NEW_OPPORTUNITY_ID) {
      setOpportunity(applyDerivedFields(createBlankOpportunity()));
      setError(null);
      setLoading(false);
      setDirtyKeys(new Set());
      setStageHistory([]);
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    setDirtyKeys(new Set());
    setStageHistory([]);
    const t = window.setTimeout(() => {
      void (async () => {
        if (loadGenRef.current !== gen) return;
        const row = crmOpportunitiesRef.current.find((o) => o.id === opportunityId);
        if (row) {
          const next = applyDerivedFields(cloneOpportunity(row));
          setOpportunity(next);
          if (!isUuid(opportunityId)) {
            setStageHistory(buildDemoStageHistory(next));
          }
          setError(null);
          if (loadGenRef.current === gen) setLoading(false);
          return;
        }
        if (isUuid(opportunityId)) {
          try {
            const apiRow = await crmGet<Record<string, unknown>>('deals', opportunityId);
            if (loadGenRef.current !== gen) return;
            const next = applyDerivedFields(mapSalesOpportunityRowToCrm(apiRow));
            setOpportunity(next);
            setError(null);
          } catch {
            if (loadGenRef.current !== gen) return;
            setOpportunity(null);
            setError(new Error('Opportunity not found.'));
          }
        } else {
          setOpportunity(null);
          setError(new Error('Opportunity not found.'));
        }
        if (loadGenRef.current === gen) setLoading(false);
      })();
    }, 120);
    return () => window.clearTimeout(t);
  }, [opportunityId, loadRetryNonce]);

  const retryLoad = useCallback(() => {
    setLoadRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!opportunityId || opportunityId === CRM_NEW_OPPORTUNITY_ID || !isUuid(opportunityId)) {
      return;
    }
    const gen = ++histGenRef.current;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchDealStageHistory(opportunityId);
        if (cancelled || gen !== histGenRef.current) return;
        setStageHistory(rows);
      } catch {
        if (cancelled || gen !== histGenRef.current) return;
        setStageHistory([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opportunityId, opportunity?.stage, opportunity?.updatedAtISO]);

  useEffect(() => {
    opportunityRef.current = opportunity;
  }, [opportunity]);

  const updateField = useCallback(<K extends keyof Opportunity>(key: K, value: Opportunity[K]) => {
    setOpportunity((prev) => {
      if (!prev) return prev;
      const next: Opportunity = { ...prev, [key]: value };
      if (key === 'ownerId' && typeof value === 'string') {
        const o = MOCK_SCHEDULE_OWNERS.find((x) => x.id === value);
        next.ownerName = o?.name ?? next.ownerName;
      }
      if (key === 'territoryId') {
        if (value === '' || value === undefined) {
          next.territoryId = undefined;
          next.territoryName = undefined;
        } else if (typeof value === 'string') {
          next.territoryName = territoryLabel(value);
        }
      }
      return applyDerivedFields(next);
    });
    setDirtyKeys((d) => new Set(d).add(String(key)));
  }, [territoryLabel]);

  const clearDirtyKeys = useCallback((keys: string[]) => {
    setDirtyKeys((d) => {
      const next = new Set(d);
      keys.forEach((k) => next.delete(k));
      return next;
    });
  }, []);

  const savePartial = useCallback(
    async (patch: Partial<Opportunity>) => {
      if (!opportunityId) return;
      const keys = Object.keys(patch) as string[];
      const snap = opportunityRef.current ? cloneOpportunity(opportunityRef.current) : null;
      const persistPayloadHolder: { payload: Partial<Opportunity> } = {
        payload: { ...patch, id: opportunityId },
      };
      setOpportunity((prev) => {
        if (!prev) return prev;
        const next: Opportunity = { ...prev, ...patch };
        if ('ownerId' in patch && patch.ownerId) {
          const o = MOCK_SCHEDULE_OWNERS.find((x) => x.id === patch.ownerId);
          if (o) next.ownerName = o.name;
        }
        if ('territoryId' in patch) {
          if (!patch.territoryId) {
            next.territoryId = undefined;
            next.territoryName = undefined;
          } else {
            next.territoryName = territoryLabel(patch.territoryId);
          }
        }
        if ('primaryContactId' in patch) {
          const raw = patch.primaryContactId;
          next.primaryContactId = raw != null && String(raw).trim() !== '' ? String(raw).trim() : undefined;
          if ('primaryContactName' in patch) {
            next.primaryContactName = patch.primaryContactName?.trim() || undefined;
          }
        }
        const applied = applyDerivedFields(next);
        let persistPayload: Partial<Opportunity> = { ...patch, id: opportunityId };
        if (
          opportunityId === CRM_NEW_OPPORTUNITY_ID &&
          !('name' in patch) &&
          applied.name.trim()
        ) {
          persistPayload = { ...persistPayload, name: applied.name };
        }
        persistPayloadHolder.payload = persistPayload;
        return applied;
      });
      /** `__crm_new__` draft never hits the API on blur or per-field save — only **`commitNewDeal`** POSTs. */
      if (opportunityId === CRM_NEW_OPPORTUNITY_ID) {
        clearDirtyKeys(keys);
        return;
      }
      try {
        const server = await onSave(persistPayloadHolder.payload);
        if (
          server &&
          typeof server === 'object' &&
          'id' in server &&
          typeof (server as Opportunity).id === 'string'
        ) {
          setOpportunity(applyDerivedFields(cloneOpportunity(server as Opportunity)));
        }
        clearDirtyKeys(keys);
      } catch (e) {
        if (snap) {
          setOpportunity(snap);
        } else {
          const row = crmOpportunitiesRef.current.find((o) => o.id === opportunityId);
          setOpportunity(row ? applyDerivedFields(cloneOpportunity(row)) : null);
        }
        throw e;
      }
    },
    [onSave, clearDirtyKeys, opportunityId, territoryLabel]
  );

  const saveAll = useCallback(async () => {
    if (!opportunity) return;
    const keys = [...dirtyKeys];
    if (keys.length === 0) return;
    const patch: Partial<Opportunity> = {};
    for (const k of keys) {
      const key = k as keyof Opportunity;
      const v = opportunity[key];
      if (key === 'tags' && Array.isArray(v)) {
        (patch as Record<string, unknown>).tags = [...v];
      } else {
        (patch as Record<string, unknown>)[k] = v;
      }
    }
    if (opportunity.id === CRM_NEW_OPPORTUNITY_ID) {
      setOpportunity((prev) => {
        if (!prev) return prev;
        const next: Opportunity = { ...prev, ...patch } as Opportunity;
        return applyDerivedFields(next);
      });
      setDirtyKeys(new Set());
      return;
    }
    const persistPayload: Partial<Opportunity> = { ...patch, id: opportunity.id };
    const server = await onSave(persistPayload);
    if (
      server &&
      typeof server === 'object' &&
      'id' in server &&
      typeof (server as Opportunity).id === 'string'
    ) {
      setOpportunity(applyDerivedFields(cloneOpportunity(server as Opportunity)));
    }
    setDirtyKeys(new Set());
  }, [opportunity, dirtyKeys, onSave]);

  const commitNewDeal = useCallback(async (): Promise<NewDealCommitResult> => {
    if (opportunityId !== CRM_NEW_OPPORTUNITY_ID) {
      return { ok: false, errors: { form: 'Not a new deal draft.' } };
    }
    const o = opportunityRef.current;
    if (!o) {
      return { ok: false, errors: { form: 'Nothing to save.' } };
    }
    const errors: NewDealCommitErrors = {};
    const name = (o.name ?? '').trim();
    if (!name || name === '—') {
      errors.name = 'Enter a deal name.';
    }
    if (!(o.accountId && String(o.accountId).trim())) {
      errors.accountId = 'Select an account.';
    }
    if (!o.closeDateISO || String(o.closeDateISO).trim().length < 10) {
      errors.closeDateISO = 'Choose a close date.';
    }
    const amt = Number(o.amount);
    if (!Number.isFinite(amt) || amt < 0) {
      errors.amount = 'Enter a valid amount (0 or greater).';
    }
    if (Object.keys(errors).length > 0) {
      return { ok: false, errors };
    }
    try {
      const server = await onSave({
        id: CRM_NEW_OPPORTUNITY_ID,
        name,
        accountId: o.accountId,
        accountName: o.accountName,
        primaryContactId: o.primaryContactId,
        primaryContactName: o.primaryContactName,
        ownerId: o.ownerId,
        ownerName: o.ownerName,
        stage: o.stage,
        amount: amt,
        probability: o.probability,
        closeDateISO: o.closeDateISO,
        source: o.source,
        territoryId: o.territoryId,
        territoryName: o.territoryName,
        nextStep: o.nextStep,
        notes: o.notes,
        tags: o.tags,
        meddpiccMetrics: o.meddpiccMetrics,
        meddpiccEconomicBuyer: o.meddpiccEconomicBuyer,
        meddpiccDecisionProcess: o.meddpiccDecisionProcess,
        meddpiccDecisionCriteria: o.meddpiccDecisionCriteria,
        meddpiccPaperProcess: o.meddpiccPaperProcess,
        meddpiccIdentifiedPain: o.meddpiccIdentifiedPain,
        meddpiccChampion: o.meddpiccChampion,
        meddpiccCompetition: o.meddpiccCompetition,
        threeWhysAnything: o.threeWhysAnything,
        threeWhysYourCompany: o.threeWhysYourCompany,
        threeWhysNow: o.threeWhysNow,
        valueFrameworkCurrentSituation: o.valueFrameworkCurrentSituation,
        valueFrameworkNegativeConsequences: o.valueFrameworkNegativeConsequences,
        valueFrameworkPositiveOutcomes: o.valueFrameworkPositiveOutcomes,
        valueFrameworkRequiredCapabilities: o.valueFrameworkRequiredCapabilities,
        valueFrameworkHowWeDoIt: o.valueFrameworkHowWeDoIt,
        valueFrameworkHowWeDoItBetter: o.valueFrameworkHowWeDoItBetter,
        valueFrameworkProofPoints: o.valueFrameworkProofPoints,
      });
      if (
        server &&
        typeof server === 'object' &&
        'id' in server &&
        typeof (server as Opportunity).id === 'string'
      ) {
        const mapped = applyDerivedFields(cloneOpportunity(server as Opportunity));
        setOpportunity(mapped);
        setDirtyKeys(new Set());
        return { ok: true, opportunity: mapped };
      }
      return { ok: false, errors: { form: 'Could not create deal.' } };
    } catch (e) {
      if (isCrmConflictError(e)) {
        return {
          ok: false,
          errors: {
            name: 'A deal with this name already exists.',
            form: 'Choose a different name or open the existing deal.',
            existingDealId: e.body.existing_id,
          },
        };
      }
      const msg = e instanceof Error ? e.message : 'Could not create deal.';
      return { ok: false, errors: { form: msg } };
    }
  }, [onSave, opportunityId]);

  const isDirty = useCallback((key: keyof Opportunity | string) => dirtyKeys.has(String(key)), [dirtyKeys]);

  const hasUnsavedChanges = dirtyKeys.size > 0;

  return {
    opportunity,
    loading,
    error,
    retryLoad,
    stageHistory,
    updateField,
    saveAll,
    savePartial,
    commitNewDeal,
    isDirty,
    hasUnsavedChanges,
  };
}
